import { supabase } from '../lib/supabase';
import { UserContext } from '../contexts/EmbedContext';

export interface SubscriptionLimits {
  documentsAllowed: number;
  documentsUsed: number;
  canGenerateDocuments: boolean;
  tierName: string;
  resetDate: string;
}

const SUBSCRIPTION_TIER_LIMITS = {
  free: { documents: 1, name: 'Free' },
  basic: { documents: 10, name: 'Basic' },
  professional: { documents: -1, name: 'Professional' },
  enterprise: { documents: -1, name: 'Enterprise' },
};

class UserContextService {
  async getSubscriptionLimits(userId: string): Promise<SubscriptionLimits> {
    try {
      const { data, error } = await supabase
        .from('subscription_limits')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        const newLimits = await this.createSubscriptionLimits(userId, 'free');
        return newLimits;
      }

      const tierConfig = SUBSCRIPTION_TIER_LIMITS[data.subscription_tier as keyof typeof SUBSCRIPTION_TIER_LIMITS];
      const maxDocuments = data.custom_limits?.maxDocuments ?? tierConfig.documents;
      const canGenerate = maxDocuments === -1 || data.documents_generated < maxDocuments;

      return {
        documentsAllowed: maxDocuments,
        documentsUsed: data.documents_generated,
        canGenerateDocuments: canGenerate,
        tierName: tierConfig.name,
        resetDate: data.period_end_date,
      };
    } catch (error) {
      console.error('Error fetching subscription limits:', error);
      return {
        documentsAllowed: 1,
        documentsUsed: 0,
        canGenerateDocuments: true,
        tierName: 'Free',
        resetDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      };
    }
  }

  async createSubscriptionLimits(userId: string, tier: string): Promise<SubscriptionLimits> {
    const periodStart = new Date();
    const periodEnd = new Date(periodStart.getTime() + 30 * 24 * 60 * 60 * 1000);

    const { data, error } = await supabase
      .from('subscription_limits')
      .insert({
        user_id: userId,
        subscription_tier: tier,
        documents_generated: 0,
        period_start_date: periodStart.toISOString(),
        period_end_date: periodEnd.toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    const tierConfig = SUBSCRIPTION_TIER_LIMITS[tier as keyof typeof SUBSCRIPTION_TIER_LIMITS];

    return {
      documentsAllowed: tierConfig.documents,
      documentsUsed: 0,
      canGenerateDocuments: true,
      tierName: tierConfig.name,
      resetDate: periodEnd.toISOString(),
    };
  }

  async incrementDocumentCount(userId: string): Promise<void> {
    try {
      const { data: currentData } = await supabase
        .from('subscription_limits')
        .select('documents_generated')
        .eq('user_id', userId)
        .maybeSingle();

      if (currentData) {
        await supabase
          .from('subscription_limits')
          .update({
            documents_generated: currentData.documents_generated + 1,
          })
          .eq('user_id', userId);
      }
    } catch (error) {
      console.error('Error incrementing document count:', error);
    }
  }

  async checkAndResetPeriod(userId: string): Promise<void> {
    try {
      const { data } = await supabase
        .from('subscription_limits')
        .select('period_end_date')
        .eq('user_id', userId)
        .maybeSingle();

      if (data && new Date(data.period_end_date) < new Date()) {
        const newPeriodStart = new Date();
        const newPeriodEnd = new Date(newPeriodStart.getTime() + 30 * 24 * 60 * 60 * 1000);

        await supabase
          .from('subscription_limits')
          .update({
            documents_generated: 0,
            period_start_date: newPeriodStart.toISOString(),
            period_end_date: newPeriodEnd.toISOString(),
          })
          .eq('user_id', userId);
      }
    } catch (error) {
      console.error('Error checking/resetting period:', error);
    }
  }

  async saveProperty(userId: string, address: string, propertyType: string, metadata?: Record<string, any>) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .insert({
          user_id: userId,
          address,
          property_type: propertyType,
          metadata: metadata || {},
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving property:', error);
      throw error;
    }
  }

  async getUserProperties(userId: string) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user properties:', error);
      return [];
    }
  }

  getUserSessionId(): string {
    let sessionId = localStorage.getItem('user_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('user_session_id', sessionId);
    }
    return sessionId;
  }

  getUserIdOrSession(userContext: UserContext | null): string {
    return userContext?.userId || this.getUserSessionId();
  }
}

export const userContextService = new UserContextService();
